/**
 * CoachPlaybook - Football Sport Module
 * Version: 0.6.0
 * Description: Complete football configuration — positions, actions, formations, age groups
 */

const football = {
  id: 'football',
  name: 'Football',
  icon: '🏈',
  accentColor: '#7c3aed',
  surfaceType: 'field',

  // Field views available
  views: ['full', 'red-zone', 'goal-line', 'formation'],
  defaultView: 'formation',
  orientation: ['vertical', 'horizontal'],

  // Default player counts
  playersPerSide: 11,
  maxDefenders: 11,

  // Age group configurations
  ageGroups: [
    { id: 'flag', label: 'Flag (U8)', players: 5, fieldSize: 'reduced', quarters: 4, quarterLength: 8, playsClock: false },
    { id: 'peewee', label: 'Pee Wee', players: 11, fieldSize: 'reduced', quarters: 4, quarterLength: 8, playsClock: true },
    { id: 'youth', label: 'Youth', players: 11, fieldSize: 'full', quarters: 4, quarterLength: 8, playsClock: true },
    { id: 'middle', label: 'Middle School', players: 11, fieldSize: 'full', quarters: 4, quarterLength: 8, playsClock: true },
    { id: 'jv', label: 'JV', players: 11, fieldSize: 'full', quarters: 4, quarterLength: 10, playsClock: true },
    { id: 'varsity', label: 'Varsity', players: 11, fieldSize: 'full', quarters: 4, quarterLength: 12, playsClock: true },
    { id: 'college', label: 'College', players: 11, fieldSize: 'full', quarters: 4, quarterLength: 15, playsClock: true },
    { id: 'pro', label: 'Pro/NFL', players: 11, fieldSize: 'full', quarters: 4, quarterLength: 15, playsClock: true }
  ],

  // Positions organized by unit
  positions: [
    // Offense
    { id: 'QB', name: 'Quarterback', short: 'QB', unit: 'offense', defaultColor: '#ef4444', role: 'offense' },
    { id: 'RB', name: 'Running Back', short: 'RB', unit: 'offense', defaultColor: '#22c55e', role: 'offense' },
    { id: 'FB', name: 'Fullback', short: 'FB', unit: 'offense', defaultColor: '#16a34a', role: 'offense' },
    { id: 'HB', name: 'Halfback', short: 'HB', unit: 'offense', defaultColor: '#15803d', role: 'offense' },
    { id: 'WR-X', name: 'Wide Receiver (X)', short: 'X', unit: 'offense', defaultColor: '#3b82f6', role: 'offense' },
    { id: 'WR-Z', name: 'Wide Receiver (Z)', short: 'Z', unit: 'offense', defaultColor: '#2563eb', role: 'offense' },
    { id: 'WR-SLOT', name: 'Slot Receiver', short: 'Slot', unit: 'offense', defaultColor: '#1d4ed8', role: 'offense' },
    { id: 'TE', name: 'Tight End', short: 'TE', unit: 'offense', defaultColor: '#f59e0b', role: 'offense' },
    { id: 'LT', name: 'Left Tackle', short: 'LT', unit: 'offense', defaultColor: '#a3a3a3', role: 'offense' },
    { id: 'LG', name: 'Left Guard', short: 'LG', unit: 'offense', defaultColor: '#a3a3a3', role: 'offense' },
    { id: 'C', name: 'Center', short: 'C', unit: 'offense', defaultColor: '#a3a3a3', role: 'offense' },
    { id: 'RG', name: 'Right Guard', short: 'RG', unit: 'offense', defaultColor: '#a3a3a3', role: 'offense' },
    { id: 'RT', name: 'Right Tackle', short: 'RT', unit: 'offense', defaultColor: '#a3a3a3', role: 'offense' },

    // Defense
    { id: 'DE', name: 'Defensive End', short: 'DE', unit: 'defense', defaultColor: '#dc2626', role: 'defense' },
    { id: 'DT', name: 'Defensive Tackle', short: 'DT', unit: 'defense', defaultColor: '#b91c1c', role: 'defense' },
    { id: 'NT', name: 'Nose Tackle', short: 'NT', unit: 'defense', defaultColor: '#991b1b', role: 'defense' },
    { id: 'OLB', name: 'Outside Linebacker', short: 'OLB', unit: 'defense', defaultColor: '#f97316', role: 'defense' },
    { id: 'ILB', name: 'Inside Linebacker', short: 'ILB', unit: 'defense', defaultColor: '#ea580c', role: 'defense' },
    { id: 'MLB', name: 'Middle Linebacker', short: 'MLB', unit: 'defense', defaultColor: '#c2410c', role: 'defense' },
    { id: 'CB', name: 'Cornerback', short: 'CB', unit: 'defense', defaultColor: '#8b5cf6', role: 'defense' },
    { id: 'FS', name: 'Free Safety', short: 'FS', unit: 'defense', defaultColor: '#6d28d9', role: 'defense' },
    { id: 'SS', name: 'Strong Safety', short: 'SS', unit: 'defense', defaultColor: '#5b21b6', role: 'defense' },
    { id: 'NB', name: 'Nickel Back', short: 'NB', unit: 'defense', defaultColor: '#7c3aed', role: 'defense' },
    { id: 'DB', name: 'Dime Back', short: 'DB', unit: 'defense', defaultColor: '#6366f1', role: 'defense' },

    // Special Teams
    { id: 'K', name: 'Kicker', short: 'K', unit: 'special', defaultColor: '#06b6d4', role: 'special' },
    { id: 'P', name: 'Punter', short: 'P', unit: 'special', defaultColor: '#0891b2', role: 'special' },
    { id: 'LS', name: 'Long Snapper', short: 'LS', unit: 'special', defaultColor: '#0e7490', role: 'special' },
    { id: 'KR', name: 'Kick Returner', short: 'KR', unit: 'special', defaultColor: '#14b8a6', role: 'special' },
    { id: 'PR', name: 'Punt Returner', short: 'PR', unit: 'special', defaultColor: '#0d9488', role: 'special' },
    { id: 'GUN', name: 'Gunner', short: 'GUN', unit: 'special', defaultColor: '#0f766e', role: 'special' },
    { id: 'JAM', name: 'Jammer', short: 'JAM', unit: 'special', defaultColor: '#115e59', role: 'special' },
    { id: 'PP', name: 'Personal Protector', short: 'PP', unit: 'special', defaultColor: '#134e4a', role: 'special' },
    { id: 'H', name: 'Holder', short: 'H', unit: 'special', defaultColor: '#155e75', role: 'special' },
    { id: 'UB', name: 'Upback', short: 'UB', unit: 'special', defaultColor: '#164e63', role: 'special' }
  ],

  // Default formation (I-Formation, percentage-based coordinates)
  defaultFormation: {
    name: 'I-Formation',
    positions: {
      QB:  { x: 50, y: 62, hasBall: true },
      FB:  { x: 50, y: 52, hasBall: false },
      RB:  { x: 50, y: 42, hasBall: false },
      'WR-X': { x: 10, y: 70, hasBall: false },
      'WR-Z': { x: 90, y: 70, hasBall: false },
      TE:  { x: 72, y: 70, hasBall: false },
      LT:  { x: 38, y: 70, hasBall: false },
      LG:  { x: 44, y: 70, hasBall: false },
      C:   { x: 50, y: 70, hasBall: false },
      RG:  { x: 56, y: 70, hasBall: false },
      RT:  { x: 62, y: 70, hasBall: false }
    }
  },

  // Common formations
  formations: [
    // Offense
    {
      id: 'i-formation',
      name: 'I-Formation',
      type: 'offense',
      positions: { QB: { x: 50, y: 62 }, FB: { x: 50, y: 52 }, RB: { x: 50, y: 42 }, 'WR-X': { x: 10, y: 70 }, 'WR-Z': { x: 90, y: 70 }, TE: { x: 72, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },
    {
      id: 'pro-set',
      name: 'Pro Set',
      type: 'offense',
      positions: { QB: { x: 50, y: 62 }, RB: { x: 42, y: 52 }, FB: { x: 58, y: 52 }, 'WR-X': { x: 10, y: 70 }, 'WR-Z': { x: 90, y: 70 }, TE: { x: 72, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },
    {
      id: 'single-back',
      name: 'Single Back',
      type: 'offense',
      positions: { QB: { x: 50, y: 62 }, RB: { x: 50, y: 52 }, 'WR-X': { x: 10, y: 70 }, 'WR-Z': { x: 90, y: 70 }, 'WR-SLOT': { x: 78, y: 70 }, TE: { x: 72, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },
    {
      id: 'shotgun',
      name: 'Shotgun',
      type: 'offense',
      positions: { QB: { x: 50, y: 55 }, RB: { x: 42, y: 55 }, 'WR-X': { x: 8, y: 70 }, 'WR-Z': { x: 92, y: 70 }, 'WR-SLOT': { x: 78, y: 70 }, TE: { x: 72, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },
    {
      id: 'pistol',
      name: 'Pistol',
      type: 'offense',
      positions: { QB: { x: 50, y: 58 }, RB: { x: 50, y: 48 }, 'WR-X': { x: 10, y: 70 }, 'WR-Z': { x: 90, y: 70 }, 'WR-SLOT': { x: 22, y: 70 }, TE: { x: 72, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },
    {
      id: 'spread',
      name: 'Spread',
      type: 'offense',
      positions: { QB: { x: 50, y: 55 }, RB: { x: 42, y: 55 }, 'WR-X': { x: 5, y: 70 }, 'WR-Z': { x: 95, y: 70 }, 'WR-SLOT': { x: 22, y: 70 }, TE: { x: 78, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },
    {
      id: 'empty',
      name: 'Empty',
      type: 'offense',
      positions: { QB: { x: 50, y: 55 }, 'WR-X': { x: 5, y: 70 }, 'WR-Z': { x: 95, y: 70 }, 'WR-SLOT': { x: 22, y: 70 }, RB: { x: 78, y: 70 }, TE: { x: 72, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },
    {
      id: 'wildcat',
      name: 'Wildcat',
      type: 'offense',
      positions: { RB: { x: 50, y: 58 }, QB: { x: 78, y: 70 }, HB: { x: 42, y: 52 }, 'WR-X': { x: 5, y: 70 }, 'WR-Z': { x: 95, y: 70 }, TE: { x: 72, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },
    {
      id: 'wing-t',
      name: 'Wing-T',
      type: 'offense',
      positions: { QB: { x: 50, y: 62 }, FB: { x: 50, y: 52 }, HB: { x: 35, y: 55 }, RB: { x: 70, y: 68 }, 'WR-X': { x: 10, y: 70 }, TE: { x: 72, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },
    {
      id: 'wishbone',
      name: 'Wishbone',
      type: 'offense',
      positions: { QB: { x: 50, y: 62 }, FB: { x: 50, y: 52 }, HB: { x: 40, y: 45 }, RB: { x: 60, y: 45 }, 'WR-X': { x: 10, y: 70 }, TE: { x: 72, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },
    {
      id: 'triple-option',
      name: 'Triple Option',
      type: 'offense',
      positions: { QB: { x: 50, y: 62 }, FB: { x: 50, y: 52 }, HB: { x: 38, y: 50 }, 'WR-X': { x: 10, y: 70 }, 'WR-Z': { x: 90, y: 70 }, TE: { x: 72, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },
    {
      id: 'trips',
      name: 'Trips',
      type: 'offense',
      positions: { QB: { x: 50, y: 55 }, RB: { x: 42, y: 55 }, 'WR-X': { x: 10, y: 70 }, 'WR-Z': { x: 92, y: 70 }, 'WR-SLOT': { x: 82, y: 70 }, TE: { x: 72, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },
    {
      id: 'bunch',
      name: 'Bunch',
      type: 'offense',
      positions: { QB: { x: 50, y: 55 }, RB: { x: 42, y: 55 }, 'WR-X': { x: 10, y: 70 }, 'WR-Z': { x: 82, y: 68 }, 'WR-SLOT': { x: 80, y: 72 }, TE: { x: 78, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },
    {
      id: 'ace',
      name: 'Ace',
      type: 'offense',
      positions: { QB: { x: 50, y: 62 }, RB: { x: 50, y: 52 }, 'WR-X': { x: 10, y: 70 }, 'WR-Z': { x: 90, y: 70 }, TE: { x: 72, y: 70 }, 'WR-SLOT': { x: 22, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },
    {
      id: 'gun-trips',
      name: 'Gun Trips',
      type: 'offense',
      positions: { QB: { x: 50, y: 55 }, RB: { x: 42, y: 55 }, 'WR-X': { x: 10, y: 70 }, 'WR-Z': { x: 95, y: 70 }, 'WR-SLOT': { x: 85, y: 70 }, TE: { x: 75, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },
    {
      id: '11-personnel',
      name: '11 Personnel (1 RB, 1 TE)',
      type: 'offense',
      personnel: { RB: 1, TE: 1, WR: 3 },
      positions: { QB: { x: 50, y: 55 }, RB: { x: 42, y: 55 }, 'WR-X': { x: 8, y: 70 }, 'WR-Z': { x: 92, y: 70 }, 'WR-SLOT': { x: 78, y: 70 }, TE: { x: 72, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },
    {
      id: '12-personnel',
      name: '12 Personnel (1 RB, 2 TE)',
      type: 'offense',
      personnel: { RB: 1, TE: 2, WR: 2 },
      positions: { QB: { x: 50, y: 62 }, RB: { x: 50, y: 52 }, 'WR-X': { x: 10, y: 70 }, 'WR-Z': { x: 90, y: 70 }, TE: { x: 72, y: 70 }, 'TE-2': { x: 28, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },
    {
      id: '21-personnel',
      name: '21 Personnel (2 RB, 1 TE)',
      type: 'offense',
      personnel: { RB: 2, TE: 1, WR: 2 },
      positions: { QB: { x: 50, y: 62 }, FB: { x: 50, y: 52 }, RB: { x: 50, y: 42 }, 'WR-X': { x: 10, y: 70 }, 'WR-Z': { x: 90, y: 70 }, TE: { x: 72, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },
    {
      id: '22-personnel',
      name: '22 Personnel (2 RB, 2 TE)',
      type: 'offense',
      personnel: { RB: 2, TE: 2, WR: 1 },
      positions: { QB: { x: 50, y: 62 }, FB: { x: 50, y: 52 }, RB: { x: 50, y: 42 }, 'WR-X': { x: 10, y: 70 }, TE: { x: 72, y: 70 }, 'TE-2': { x: 28, y: 70 }, LT: { x: 38, y: 70 }, LG: { x: 44, y: 70 }, C: { x: 50, y: 70 }, RG: { x: 56, y: 70 }, RT: { x: 62, y: 70 } }
    },

    // Defense
    {
      id: '4-3',
      name: '4-3',
      type: 'defense',
      positions: { DE: { x: 30, y: 70 }, 'DE-2': { x: 70, y: 70 }, DT: { x: 44, y: 70 }, 'DT-2': { x: 56, y: 70 }, MLB: { x: 50, y: 58 }, OLB: { x: 25, y: 58 }, 'OLB-2': { x: 75, y: 58 }, CB: { x: 8, y: 65 }, 'CB-2': { x: 92, y: 65 }, FS: { x: 50, y: 40 }, SS: { x: 65, y: 48 } }
    },
    {
      id: '3-4',
      name: '3-4',
      type: 'defense',
      positions: { DE: { x: 35, y: 70 }, 'DE-2': { x: 65, y: 70 }, NT: { x: 50, y: 70 }, OLB: { x: 20, y: 62 }, 'OLB-2': { x: 80, y: 62 }, ILB: { x: 42, y: 58 }, 'ILB-2': { x: 58, y: 58 }, CB: { x: 8, y: 65 }, 'CB-2': { x: 92, y: 65 }, FS: { x: 50, y: 40 }, SS: { x: 65, y: 48 } }
    },
    {
      id: 'nickel-4-2-5',
      name: '4-2-5 (Nickel)',
      type: 'defense',
      positions: { DE: { x: 30, y: 70 }, 'DE-2': { x: 70, y: 70 }, DT: { x: 44, y: 70 }, 'DT-2': { x: 56, y: 70 }, MLB: { x: 42, y: 58 }, 'MLB-2': { x: 58, y: 58 }, CB: { x: 8, y: 65 }, 'CB-2': { x: 92, y: 65 }, NB: { x: 80, y: 60 }, FS: { x: 50, y: 40 }, SS: { x: 65, y: 48 } }
    },
    {
      id: '3-3-5',
      name: '3-3-5',
      type: 'defense',
      positions: { DE: { x: 35, y: 70 }, 'DE-2': { x: 65, y: 70 }, NT: { x: 50, y: 70 }, OLB: { x: 22, y: 60 }, MLB: { x: 50, y: 58 }, 'OLB-2': { x: 78, y: 60 }, CB: { x: 8, y: 65 }, 'CB-2': { x: 92, y: 65 }, NB: { x: 80, y: 55 }, FS: { x: 40, y: 40 }, SS: { x: 60, y: 40 } }
    },
    {
      id: 'dime',
      name: 'Dime',
      type: 'defense',
      positions: { DE: { x: 32, y: 70 }, 'DE-2': { x: 68, y: 70 }, DT: { x: 44, y: 70 }, 'DT-2': { x: 56, y: 70 }, MLB: { x: 50, y: 58 }, CB: { x: 8, y: 65 }, 'CB-2': { x: 92, y: 65 }, NB: { x: 22, y: 58 }, DB: { x: 78, y: 58 }, FS: { x: 50, y: 38 }, SS: { x: 65, y: 45 } }
    },
    {
      id: 'quarter',
      name: 'Quarter',
      type: 'defense',
      positions: { DE: { x: 35, y: 70 }, 'DE-2': { x: 65, y: 70 }, DT: { x: 50, y: 70 }, MLB: { x: 50, y: 58 }, CB: { x: 5, y: 62 }, 'CB-2': { x: 95, y: 62 }, NB: { x: 22, y: 58 }, DB: { x: 78, y: 58 }, FS: { x: 35, y: 38 }, SS: { x: 65, y: 38 }, 'DB-2': { x: 50, y: 42 } }
    },
    {
      id: 'bear',
      name: 'Bear',
      type: 'defense',
      positions: { DE: { x: 28, y: 70 }, 'DE-2': { x: 72, y: 70 }, DT: { x: 44, y: 70 }, NT: { x: 50, y: 70 }, 'DT-2': { x: 56, y: 70 }, MLB: { x: 50, y: 58 }, OLB: { x: 20, y: 60 }, 'OLB-2': { x: 80, y: 60 }, CB: { x: 8, y: 65 }, 'CB-2': { x: 92, y: 65 }, SS: { x: 50, y: 42 } }
    },
    {
      id: '46',
      name: '46 Defense',
      type: 'defense',
      positions: { DE: { x: 28, y: 70 }, 'DE-2': { x: 72, y: 70 }, DT: { x: 44, y: 70 }, 'DT-2': { x: 56, y: 70 }, MLB: { x: 50, y: 58 }, OLB: { x: 22, y: 60 }, 'OLB-2': { x: 78, y: 60 }, SS: { x: 65, y: 62 }, CB: { x: 8, y: 65 }, 'CB-2': { x: 92, y: 65 }, FS: { x: 50, y: 38 } }
    },
    {
      id: 'cover-0',
      name: 'Cover 0',
      type: 'defense',
      description: 'Man coverage, no deep safety, all-out blitz look'
    },
    {
      id: 'cover-1',
      name: 'Cover 1',
      type: 'defense',
      description: 'Man coverage with single high safety'
    },
    {
      id: 'cover-2',
      name: 'Cover 2',
      type: 'defense',
      description: 'Two deep safeties splitting the field'
    },
    {
      id: 'cover-3',
      name: 'Cover 3',
      type: 'defense',
      description: 'Three deep defenders splitting field into thirds'
    },
    {
      id: 'cover-4',
      name: 'Cover 4',
      type: 'defense',
      description: 'Four deep defenders in quarter coverage'
    },
    {
      id: 'cover-6',
      name: 'Cover 6',
      type: 'defense',
      description: 'Cover 4 on one side, Cover 2 on the other'
    }
  ],

  // Complete action library
  actions: {
    offense: [
      // Ball handling
      { id: 'snap', name: 'Snap', icon: '🏈', lineStyle: 'arrow', category: 'ball' },
      { id: 'handoff', name: 'Handoff', icon: '🤝', lineStyle: 'arrow-short', category: 'ball' },
      { id: 'toss', name: 'Toss', icon: '🔄', lineStyle: 'arrow', category: 'ball' },
      { id: 'pitch', name: 'Pitch', icon: '↗️', lineStyle: 'arrow', category: 'ball' },
      { id: 'pass', name: 'Pass', icon: '➡️', lineStyle: 'arrow', category: 'ball' },
      { id: 'throw', name: 'Throw', icon: '🎯', lineStyle: 'arrow', category: 'ball' },
      { id: 'screen-pass', name: 'Screen Pass', icon: '📺', lineStyle: 'arrow-lob', category: 'ball' },
      { id: 'play-action', name: 'Play-Action', icon: '🎭', lineStyle: 'arrow', category: 'ball' },
      { id: 'rpo', name: 'RPO', icon: '⚡', lineStyle: 'arrow', category: 'ball' },
      { id: 'scramble', name: 'Scramble', icon: '💨', lineStyle: 'solid', category: 'movement' },
      { id: 'audible', name: 'Audible', icon: '📢', lineStyle: 'none', category: 'command' },

      // Pre-snap movement
      { id: 'motion', name: 'Motion', icon: '↔️', lineStyle: 'dashed', category: 'pre-snap' },
      { id: 'shift', name: 'Shift', icon: '🔀', lineStyle: 'dashed', category: 'pre-snap' },

      // Offensive line
      { id: 'pull', name: 'Pull (OL)', icon: '🔄', lineStyle: 'solid', category: 'blocking' },
      { id: 'trap-block', name: 'Trap Block', icon: '🪤', lineStyle: 'solid', category: 'blocking' },
      { id: 'reach-block', name: 'Reach Block', icon: '🤲', lineStyle: 'solid', category: 'blocking' },
      { id: 'zone-block', name: 'Zone Block', icon: '➡️', lineStyle: 'solid', category: 'blocking' },
      { id: 'pass-protect', name: 'Pass Protect', icon: '🛡️', lineStyle: 'solid', category: 'blocking' },
      { id: 'chip-block', name: 'Chip Block', icon: '✊', lineStyle: 'solid', category: 'blocking' },
      { id: 'lead-block', name: 'Lead Block', icon: '🏃', lineStyle: 'solid', category: 'blocking' },

      // Route tree
      { id: 'route-flat', name: 'Flat', icon: '↘️', lineStyle: 'solid', category: 'route' },
      { id: 'route-slant', name: 'Slant', icon: '↗️', lineStyle: 'solid', category: 'route' },
      { id: 'route-out', name: 'Out', icon: '➡️', lineStyle: 'solid', category: 'route' },
      { id: 'route-in', name: 'In / Drag', icon: '⬅️', lineStyle: 'solid', category: 'route' },
      { id: 'route-dig', name: 'Dig', icon: '↩️', lineStyle: 'solid', category: 'route' },
      { id: 'route-post', name: 'Post', icon: '↖️', lineStyle: 'solid', category: 'route' },
      { id: 'route-corner', name: 'Corner', icon: '↗️', lineStyle: 'solid', category: 'route' },
      { id: 'route-go', name: 'Go / Streak', icon: '⬆️', lineStyle: 'solid', category: 'route' },
      { id: 'route-comeback', name: 'Comeback', icon: '🔙', lineStyle: 'solid', category: 'route' },
      { id: 'route-curl', name: 'Curl', icon: '🌀', lineStyle: 'solid', category: 'route' },
      { id: 'route-hitch', name: 'Hitch', icon: '⏸️', lineStyle: 'solid', category: 'route' },
      { id: 'route-seam', name: 'Seam', icon: '⬆️', lineStyle: 'solid', category: 'route' },
      { id: 'route-wheel', name: 'Wheel', icon: '🔄', lineStyle: 'curved', category: 'route' },
      { id: 'route-angle', name: 'Angle', icon: '📐', lineStyle: 'solid', category: 'route' },
      { id: 'route-option', name: 'Option Route', icon: '❓', lineStyle: 'dashed', category: 'route' },
      { id: 'route-double-move', name: 'Double Move', icon: '🔁', lineStyle: 'zigzag', category: 'route' },
      { id: 'route-sluggo', name: 'Sluggo', icon: '💫', lineStyle: 'zigzag', category: 'route' }
    ],
    defense: [
      // Pass rush
      { id: 'rush', name: 'Rush', icon: '🏃', lineStyle: 'solid', category: 'rush' },
      { id: 'contain', name: 'Contain', icon: '🛡️', lineStyle: 'solid', category: 'rush' },
      { id: 'spy', name: 'Spy', icon: '👁️', lineStyle: 'dashed', category: 'rush' },
      { id: 'edge-rush', name: 'Edge Rush', icon: '⚡', lineStyle: 'solid', category: 'rush' },
      { id: 'bull-rush', name: 'Bull Rush', icon: '🐂', lineStyle: 'solid', category: 'rush' },
      { id: 'swim-move', name: 'Swim Move', icon: '🏊', lineStyle: 'curved', category: 'rush' },
      { id: 'rip-move', name: 'Rip Move', icon: '💪', lineStyle: 'curved', category: 'rush' },
      { id: 'spin-move', name: 'Spin Move', icon: '🌀', lineStyle: 'curved', category: 'rush' },

      // Coverage
      { id: 'zone-drop', name: 'Zone Drop', icon: '⬇️', lineStyle: 'dashed', category: 'coverage' },
      { id: 'man-coverage', name: 'Man Coverage', icon: '🎯', lineStyle: 'dashed', category: 'coverage' },
      { id: 'press-coverage', name: 'Press Coverage', icon: '✋', lineStyle: 'solid', category: 'coverage' },
      { id: 'bracket-coverage', name: 'Bracket Coverage', icon: '🔒', lineStyle: 'dashed', category: 'coverage' },
      { id: 'safety-help', name: 'Safety Help Over Top', icon: '☂️', lineStyle: 'dashed', category: 'coverage' },
      { id: 'robber', name: 'Robber', icon: '🦹', lineStyle: 'dashed', category: 'coverage' },

      // Blitz
      { id: 'blitz', name: 'Blitz', icon: '⚡', lineStyle: 'solid', category: 'blitz' },
      { id: 'a-gap-blitz', name: 'A Gap Blitz', icon: '🅰️', lineStyle: 'solid', category: 'blitz' },
      { id: 'b-gap-blitz', name: 'B Gap Blitz', icon: '🅱️', lineStyle: 'solid', category: 'blitz' },

      // Run defense
      { id: 'force', name: 'Force', icon: '💥', lineStyle: 'solid', category: 'run-defense' },
      { id: 'fill', name: 'Fill', icon: '🧱', lineStyle: 'solid', category: 'run-defense' },
      { id: 'scrape', name: 'Scrape', icon: '↗️', lineStyle: 'solid', category: 'run-defense' },

      // Team defense
      { id: 'rotate', name: 'Rotation', icon: '🔄', lineStyle: 'dashed', category: 'team' },
      { id: 'switch', name: 'Switch', icon: '🔀', lineStyle: 'dashed', category: 'team' }
    ],
    specialTeams: [
      { id: 'kick', name: 'Kick', icon: '🦶', lineStyle: 'arrow-lob', category: 'kicking' },
      { id: 'punt', name: 'Punt', icon: '🏈', lineStyle: 'arrow-lob', category: 'kicking' },
      { id: 'field-goal', name: 'Field Goal', icon: '🥅', lineStyle: 'arrow-lob', category: 'kicking' },
      { id: 'onside-kick', name: 'Onside Kick', icon: '🔄', lineStyle: 'arrow', category: 'kicking' },
      { id: 'squib', name: 'Squib Kick', icon: '🏈⬇️', lineStyle: 'arrow', category: 'kicking' },
      { id: 'pooch', name: 'Pooch Kick', icon: '☁️', lineStyle: 'arrow-lob', category: 'kicking' },
      { id: 'return', name: 'Return', icon: '🏃', lineStyle: 'solid', category: 'return' },
      { id: 'fair-catch', name: 'Fair Catch', icon: '✋', lineStyle: 'none', category: 'return' },
      { id: 'block-attempt', name: 'Block Attempt', icon: '🖐️', lineStyle: 'solid', category: 'rush' },
      { id: 'personal-protect', name: 'Personal Protect', icon: '🛡️', lineStyle: 'solid', category: 'blocking' }
    ]
  },

  // Play categories
  playTypes: {
    offense: [
      { id: 'run-inside', label: 'Inside Run' },
      { id: 'run-outside', label: 'Outside Run' },
      { id: 'run-counter', label: 'Counter / Misdirection' },
      { id: 'run-draw', label: 'Draw' },
      { id: 'run-option', label: 'Option' },
      { id: 'run-power', label: 'Power' },
      { id: 'run-zone', label: 'Zone Run' },
      { id: 'run-sweep', label: 'Sweep' },
      { id: 'pass-short', label: 'Short Pass' },
      { id: 'pass-medium', label: 'Medium Pass' },
      { id: 'pass-deep', label: 'Deep Pass' },
      { id: 'pass-screen', label: 'Screen Pass' },
      { id: 'pass-play-action', label: 'Play-Action' },
      { id: 'pass-rpo', label: 'RPO' },
      { id: 'pass-bootleg', label: 'Bootleg' },
      { id: 'pass-rollout', label: 'Rollout' },
      { id: 'pass-quick-game', label: 'Quick Game' },
      { id: 'trick-play', label: 'Trick Play' },
      { id: 'hail-mary', label: 'Hail Mary' }
    ],
    defense: [
      { id: 'man-coverage', label: 'Man Coverage' },
      { id: 'zone-coverage', label: 'Zone Coverage' },
      { id: 'blitz', label: 'Blitz' },
      { id: 'zone-blitz', label: 'Zone Blitz' },
      { id: 'prevent', label: 'Prevent' },
      { id: 'red-zone-defense', label: 'Red Zone Defense' },
      { id: 'goal-line-defense', label: 'Goal Line Defense' },
      { id: 'run-defense', label: 'Run Defense' },
      { id: 'pass-defense', label: 'Pass Defense' },
      { id: 'stunt', label: 'Stunt / Twist' }
    ],
    specialTeams: [
      { id: 'kickoff', label: 'Kickoff' },
      { id: 'kickoff-return', label: 'Kickoff Return' },
      { id: 'punt-coverage', label: 'Punt Coverage' },
      { id: 'punt-return', label: 'Punt Return' },
      { id: 'field-goal-unit', label: 'Field Goal Unit' },
      { id: 'field-goal-block', label: 'Field Goal Block' },
      { id: 'onside-kick', label: 'Onside Kick' },
      { id: 'fake-punt', label: 'Fake Punt' },
      { id: 'fake-fg', label: 'Fake FG' },
      { id: 'two-point', label: 'Two-Point Conversion' }
    ],
    situational: [
      { id: 'two-minute', label: 'Two-Minute Drill' },
      { id: 'goal-line', label: 'Goal Line' },
      { id: 'red-zone', label: 'Red Zone' },
      { id: 'third-short', label: '3rd and Short' },
      { id: 'third-long', label: '3rd and Long' },
      { id: 'fourth-down', label: '4th Down' },
      { id: 'kneel', label: 'Victory Formation / Kneel' }
    ]
  },

  // Stat categories for this sport
  statCategories: [
    // Passing
    { id: 'passYds', name: 'Pass Yards', short: 'PYD', type: 'counting' },
    { id: 'passComp', name: 'Completions', short: 'CMP', type: 'counting' },
    { id: 'passAtt', name: 'Pass Attempts', short: 'ATT', type: 'counting' },
    { id: 'passTd', name: 'Pass Touchdowns', short: 'PTD', type: 'counting' },
    { id: 'int', name: 'Interceptions', short: 'INT', type: 'counting' },
    { id: 'qbRating', name: 'QB Rating', short: 'RAT', type: 'calculated' },

    // Rushing
    { id: 'rushYds', name: 'Rush Yards', short: 'RYD', type: 'counting' },
    { id: 'rushAtt', name: 'Rush Attempts', short: 'CAR', type: 'counting' },
    { id: 'rushTd', name: 'Rush Touchdowns', short: 'RTD', type: 'counting' },
    { id: 'ypc', name: 'Yards Per Carry', short: 'YPC', type: 'calculated' },
    { id: 'fumbles', name: 'Fumbles', short: 'FUM', type: 'counting' },

    // Receiving
    { id: 'receptions', name: 'Receptions', short: 'REC', type: 'counting' },
    { id: 'recYds', name: 'Receiving Yards', short: 'RYDS', type: 'counting' },
    { id: 'recTd', name: 'Receiving Touchdowns', short: 'RCTD', type: 'counting' },
    { id: 'targets', name: 'Targets', short: 'TGT', type: 'counting' },
    { id: 'ypr', name: 'Yards Per Reception', short: 'YPR', type: 'calculated' },

    // Defense
    { id: 'tackles', name: 'Tackles', short: 'TKL', type: 'counting' },
    { id: 'soloTackles', name: 'Solo Tackles', short: 'SOLO', type: 'counting' },
    { id: 'assistTackles', name: 'Assist Tackles', short: 'AST', type: 'counting' },
    { id: 'sacks', name: 'Sacks', short: 'SCK', type: 'counting' },
    { id: 'tfl', name: 'Tackles For Loss', short: 'TFL', type: 'counting' },
    { id: 'defInt', name: 'Interceptions (DEF)', short: 'DINT', type: 'counting' },
    { id: 'passDeflections', name: 'Pass Deflections', short: 'PD', type: 'counting' },
    { id: 'forcedFumbles', name: 'Forced Fumbles', short: 'FF', type: 'counting' },
    { id: 'fumbleRec', name: 'Fumble Recoveries', short: 'FR', type: 'counting' },
    { id: 'qbHits', name: 'QB Hits', short: 'QBH', type: 'counting' },

    // Special Teams
    { id: 'fgMade', name: 'FG Made', short: 'FGM', type: 'counting' },
    { id: 'fgAtt', name: 'FG Attempted', short: 'FGA', type: 'counting' },
    { id: 'xpMade', name: 'XP Made', short: 'XPM', type: 'counting' },
    { id: 'puntYds', name: 'Punt Yards', short: 'PYDS', type: 'counting' },
    { id: 'kickRetYds', name: 'Kick Return Yards', short: 'KRY', type: 'counting' },
    { id: 'puntRetYds', name: 'Punt Return Yards', short: 'PRY', type: 'counting' },

    // General
    { id: 'td', name: 'Total Touchdowns', short: 'TD', type: 'counting' },
    { id: 'penalties', name: 'Penalties', short: 'PEN', type: 'counting' },
    { id: 'penaltyYds', name: 'Penalty Yards', short: 'PENY', type: 'counting' },
    { id: 'snaps', name: 'Snaps Played', short: 'SNP', type: 'counting' }
  ],

  // Scouting skill categories
  scoutingSkills: [
    { id: 'armStrength', name: 'Arm Strength', icon: '💪' },
    { id: 'accuracy', name: 'Accuracy', icon: '🎯' },
    { id: 'speed', name: 'Speed', icon: '⚡' },
    { id: 'agility', name: 'Agility', icon: '🏃' },
    { id: 'footballIQ', name: 'Football IQ', icon: '🧠' },
    { id: 'blocking', name: 'Blocking', icon: '🛡️' },
    { id: 'tackling', name: 'Tackling', icon: '🤼' },
    { id: 'coverage', name: 'Coverage', icon: '👁️' },
    { id: 'routeRunning', name: 'Route Running', icon: '🗺️' },
    { id: 'hands', name: 'Hands', icon: '🤲' }
  ],

  // Field dimensions (for SVG rendering)
  field: {
    // NFL dimensions in yards
    length: 100,
    width: 53.33,
    endZoneDepth: 10,
    totalLength: 120, // 100 + 2 end zones
    hashMarkWidth: 18.5, // from sideline (NFL)
    yardLineSpacing: 5,
    goalPostWidth: 18.5,
    // Field markings
    markings: [
      'end-zone', 'goal-line', 'yard-lines', 'hash-marks',
      'sideline', 'numbers', 'midfield-logo', 'goal-post',
      'restraining-line', 'coaches-box', 'team-area'
    ]
  }
};

export default football;
